import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "duration", "reverse", "Wrapper"],
  _excluded2 = ["align", "duration", "Wrapper"];
import { useState } from 'react';
import { percentToVW } from '../../utils/styles';
import { toPx } from '../../../utils/toPx';
import { useIsMounted } from '../../../hooks/useIsMounted';
import styled, { css } from 'foundations-theming/utils';
import { SUN_CORE_LAYER } from '../../constants';
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
// For the first frame where the incoming content is rendered, we want that content to be on-screen
// to prevent glitches if it contains any controls with autofocus (#8454). The trick here is to hide
// the incoming content under the old content. It's on-screen as far as the browser is concerned!
const onscreenRenderMixin = css(["& >:last-child{position:absolute !important;right:0 !important;z-index:", ";}"], SUN_CORE_LAYER);
const PanelNavigatorTransitionWrapper = styled.div.withConfig({
  displayName: "PanelNavigatorTransition__PanelNavigatorTransitionWrapper",
  componentId: "sc-1exv89q-0"
})(["position:relative;display:flex;flex-direction:", ";height:100%;transform:translateX( ", " );transition-property:", ";transition-duration:", "ms;transition-timing-function:cubic-bezier(0.16,0.84,0.44,1);", ";"], ({
  reverse
}) => reverse ? 'row-reverse' : 'row', ({
  distance,
  reverse,
  transitioning,
  transitionActive
}) => {
  if (transitioning) {
    if (reverse) {
      return transitionActive ? `${distance}` : '0';
    }
    return transitionActive ? `-${distance}` : '0';
  }
  return '0';
}, ({
  transitionActive
}) => transitionActive ? 'transform, width' : 'width', ({
  duration
}) => duration, ({
  transitioning,
  transitionActive
}) => transitioning && !transitionActive && onscreenRenderMixin);
const SlideCrossTransition = _ref => {
  let {
      children,
      duration,
      reverse,
      Wrapper = PanelNavigatorTransitionWrapper
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [outgoingChild, setOutgoingChild] = useState(null);
  const [prevChild, setPrevChild] = useState(children);
  const [transitioning, setTransitioning] = useState(false);
  const [transitionActive, setTransitionActive] = useState(false);
  const mountedRef = useIsMounted();
  if (prevChild.key !== children.key) {
    setTransitioning(true);
    setOutgoingChild(prevChild);
    setPrevChild(children);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!mountedRef) return;
        setTransitionActive(true);
        setTimeout(() => {
          if (!mountedRef) return;
          setOutgoingChild(null);
          setTransitioning(false);
          setTransitionActive(false);
        }, duration);
      });
    });
  }
  return /*#__PURE__*/_jsxs(Wrapper, Object.assign({
    "data-transition-complete": !transitioning,
    distance: outgoingChild ? percentToVW(toPx(outgoingChild.props.width)) : null,
    duration: duration,
    reverse: reverse,
    style: {
      width: percentToVW(children.props.width)
    },
    transitioning: transitioning,
    transitionActive: transitionActive
  }, rest, {
    children: [outgoingChild, children]
  }));
};
export default function PanelNavigatorTransition(_ref2) {
  let {
      align = 'right',
      duration = 400,
      Wrapper = PanelNavigatorTransitionWrapper
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  return /*#__PURE__*/_jsx(SlideCrossTransition, Object.assign({
    align: align,
    duration: duration,
    Wrapper: Wrapper
  }, props));
}